import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useReveal } from "../hooks/useReveal";
import { PublicNav } from "../components/public/PublicNav";
import { PublicFooter } from "../components/public/PublicFooter";
import { GalaEnterpriseCallout } from "../components/public/GalaEnterpriseCallout";
import {
  SUBSCRIPTION_PLANS,
  PUBLIC_PRICING_PLANS,
  PLAN_FEATURE_LABELS,
  buildPlanFeatureMatrix,
  formatDineInLimit,
  type PlanFeatureValue,
} from "../lib/subscription-plans";

function RevealSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const Check = ({ muted }: { muted?: boolean }) => (
  <svg
    className={`w-5 h-5 shrink-0 ${muted ? "text-gray-300" : "text-brand-500"}`}
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const Cross = () => (
  <svg className="w-5 h-5 shrink-0 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

type FeatureValue = PlanFeatureValue;

function FeatureCell({ value }: { value: FeatureValue }) {
  if (typeof value === "string") {
    return <span className="text-gray-700 text-sm font-medium">{value}</span>;
  }
  return value ? <Check /> : <Cross />;
}

function PricingPlanCard({ plan }: { plan: (typeof PUBLIC_PRICING_PLANS)[number] }) {
  const features = buildPlanFeatureMatrix(plan.features);

  return (
    <div
      className={`relative w-full h-full rounded-2xl flex flex-col transition-shadow ${
        plan.highlight
          ? "border-2 border-brand-500 shadow-lg shadow-brand-500/10"
          : "border border-gray-100 shadow-sm hover:shadow-md"
      } bg-white`}
    >
      <div className="flex flex-col flex-1 px-7 pt-7 pb-7">
        <div className="h-7 flex items-center justify-center shrink-0 mb-3">
          {plan.badge ? (
            <span className="bg-brand-500 text-white text-xs font-bold px-4 py-1 rounded-full">
              {plan.badge}
            </span>
          ) : null}
        </div>

        <div className="shrink-0">
          <span className="text-xs font-bold tracking-widest uppercase text-brand-600">
            {plan.tierLabel}
          </span>
          <h2 className="text-3xl font-extrabold text-gray-900 mt-1">{plan.name}</h2>
        </div>

        <div className="mt-5 min-h-[7.25rem] shrink-0 space-y-1">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-4xl font-black text-gray-900 leading-none">
              {plan.limits.online.freeOrdersIncluded.toLocaleString()}
            </span>
            <span className="text-gray-500 text-sm">online orders/month</span>
          </div>
          <p className="text-sm min-h-[1.25rem] text-gray-500">
            {plan.limits.dineIn.freeOrdersIncluded > 0 ? (
              <>
                +{" "}
                <span className="font-semibold text-gray-700">
                  {plan.limits.dineIn.freeOrdersIncluded.toLocaleString()}
                </span>{" "}
                dine-in orders/month
              </>
            ) : (
              <span className="text-gray-400">Dine-in ordering not included</span>
            )}
          </p>
          <p className="text-gray-400 text-xs pt-1">
            + PKR {plan.limits.online.payPerOrderAfter} per additional order
          </p>
        </div>

        <ul className="space-y-3 flex-1 mt-6 mb-8">
          {Object.entries(features).map(([key, val]) => (
            <li key={key} className="flex items-center gap-3 min-h-[1.25rem]">
              <FeatureCell value={val} />
              <span
                className={`text-sm leading-snug ${
                  typeof val === "boolean" && !val ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {PLAN_FEATURE_LABELS[key]}
              </span>
            </li>
          ))}
        </ul>

        <Link
          to="/contact"
          className={`mt-auto shrink-0 block text-center py-3 rounded-xl text-sm font-bold transition-all duration-200 ${
            plan.highlight
              ? "bg-brand-500 hover:bg-brand-600 text-white btn-glow"
              : "border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
          }`}
        >
          Contact Us to Get Started
        </Link>
      </div>
    </div>
  );
}

export function Plans() {
  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden transition-colors duration-200">
      <PublicNav />

      {/* Header */}
      <section className="pt-28 pb-16 relative overflow-hidden bg-gradient-to-b from-brand-50/60 to-white">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-48 bg-brand-200/20 blur-3xl rounded-full" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-brand-600 text-sm font-semibold tracking-widest uppercase mb-4"
          >
            Pricing Plans
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight text-gray-900"
          >
            Scale as you{" "}
            <span className="gradient-text">grow</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-5 text-gray-500 text-lg max-w-xl mx-auto leading-relaxed"
          >
            Every Cravbot plan includes the full AI ordering engine, owner dashboard, and
            API access. Choose the tier that fits your order volume.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-4 text-sm text-gray-400"
          >
            Cravbot is available by invitation only.{" "}
            <Link to="/contact" className="text-brand-600 hover:underline">
              Contact us to get started.
            </Link>
          </motion.p>
        </div>
      </section>

      {/* Plan cards */}
      <section className="pb-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {PUBLIC_PRICING_PLANS.map((plan, i) => (
              <RevealSection key={plan.planId} delay={i * 120} className="h-full w-full flex flex-col">
                <PricingPlanCard plan={plan} />
              </RevealSection>
            ))}
          </div>

          <RevealSection className="mt-8" delay={360}>
            <GalaEnterpriseCallout />
          </RevealSection>

          <RevealSection className="mt-10 text-center">
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              All standard plans are monthly billing with clear included limits.{" "}
              <Link to="/contact" className="text-brand-600 hover:underline">
                Contact us
              </Link>{" "}
              for Gala or volume pricing.
            </p>
          </RevealSection>
        </div>
      </section>

      {/* Feature comparison table */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">Full feature comparison</h2>
          </RevealSection>

          <RevealSection>
            <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
              <table className="w-full text-sm bg-white">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="py-4 px-6 text-left text-gray-500 font-medium w-2/5">Feature</th>
                    {SUBSCRIPTION_PLANS.map((t) => (
                      <th
                        key={t.planId}
                        className={`py-4 px-4 text-center font-bold ${
                          t.highlight ? "text-brand-600" : "text-gray-700"
                        }`}
                      >
                        {t.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(PLAN_FEATURE_LABELS).map((key, idx) => (
                    <tr
                      key={key}
                      className={`border-b border-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                    >
                      <td className="py-3.5 px-6 text-gray-600">{PLAN_FEATURE_LABELS[key]}</td>
                      {SUBSCRIPTION_PLANS.map((t) => (
                        <td key={t.planId} className="py-3.5 px-4 text-center">
                          <div className="flex justify-center">
                            {t.contactOnly ? (
                              <span className="text-gray-500 text-xs font-medium">Custom</span>
                            ) : (
                              <FeatureCell value={buildPlanFeatureMatrix(t.features)[key]} />
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-white">
                    <td className="py-3.5 px-6 text-gray-600">Online orders included</td>
                    {SUBSCRIPTION_PLANS.map((t) => (
                      <td key={t.planId} className="py-3.5 px-4 text-center text-gray-800 font-semibold">
                        {t.contactOnly ? "Custom" : t.limits.online.freeOrdersIncluded.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50/50">
                    <td className="py-3.5 px-6 text-gray-600">Dine-in orders included</td>
                    {SUBSCRIPTION_PLANS.map((t) => (
                      <td key={t.planId} className="py-3.5 px-4 text-center text-gray-800 font-semibold">
                        {t.contactOnly ? "Custom" : formatDineInLimit(t)}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-white">
                    <td className="py-3.5 px-6 text-gray-600">Online overage rate</td>
                    {SUBSCRIPTION_PLANS.map((t) => (
                      <td key={t.planId} className="py-3.5 px-4 text-center text-gray-700 font-medium">
                        {t.contactOnly ? "Custom" : `PKR ${t.limits.online.payPerOrderAfter}/order`}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50/50">
                    <td className="py-3.5 px-6 text-gray-600">Dine-in overage rate</td>
                    {SUBSCRIPTION_PLANS.map((t) => (
                      <td key={t.planId} className="py-3.5 px-4 text-center text-gray-700 font-medium">
                        {t.contactOnly
                          ? "Custom"
                          : t.limits.dineIn.freeOrdersIncluded > 0
                            ? `PKR ${t.limits.dineIn.payPerOrderAfter}/order`
                            : "—"}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">Frequently asked questions</h2>
          </RevealSection>
          <div className="space-y-3">
            {[
              {
                q: "How do I get access to Cravbot?",
                a: "Cravbot is available to registered restaurant businesses only. Fill out our contact form and our team will reach out to get you set up.",
              },
              {
                q: "What counts as an 'order'?",
                a: "A completed, confirmed order processed through Cravbot counts toward your monthly quota. Online and dine-in orders have separate included limits on Feast and Banquet. Cancelled or draft conversations do not count.",
              },
              {
                q: "What happens when I exceed my plan's order limit?",
                a: "Your service continues normally. Each additional online or dine-in order beyond its included limit is billed at your plan's overage rate (PKR 50 on Savor; PKR 40 on Feast; PKR 30 on Banquet).",
              },
              {
                q: "Can I integrate Cravbot with any platform?",
                a: "Yes. Cravbot provides a REST API that your development team can integrate into any platform — your website, mobile app, WhatsApp bot, or custom chat interface.",
              },
              {
                q: "Can I upgrade or downgrade my plan?",
                a: "Yes. Contact our team to change your subscription tier. Changes take effect from your next billing cycle.",
              },
            ].map(({ q, a }, i) => (
              <RevealSection key={q} delay={i * 60}>
                <details className="bg-white border border-gray-100 rounded-xl group hover:border-brand-200:border-brand-700/50 transition-colors">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none text-gray-700 hover:text-gray-900:text-white font-medium text-sm">
                    {q}
                    <svg
                      className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform duration-200 shrink-0 ml-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="px-6 pb-5 text-gray-500 text-sm leading-relaxed">{a}</p>
                </details>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealSection>
            <div className="relative rounded-2xl overflow-hidden text-center px-8 py-12 bg-brand-500">
              <div className="absolute inset-0 dot-grid opacity-15" />
              <div className="relative">
                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
                  Ready to pick your plan?
                </h2>
                <p className="text-orange-100 text-sm mb-7">
                  Contact our team and we'll have you up and running within 24 hours.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-white text-brand-600 font-bold rounded-xl hover:bg-orange-50 transition-colors text-sm shadow-sm"
                >
                  Contact Sales
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
